// React & Hooks
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

// Material UI - Components
import {
	Avatar,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	IconButton,
	InputBase,
	Skeleton,
	Stack,
	Tooltip,
	Typography
} from "@mui/material";

// Material UI - Icons
import CloseIcon from '@mui/icons-material/Close';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import SendIcon from '@mui/icons-material/Send';

// Material UI - Styles & Theme
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

// Date & Time

// API & Context
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";

// Internal - Components
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import EmojiPicker from '../EmojiPicker';
import MessagesList from '../MessagesList';
import RescheduleTicket from '../RescheduleTicket';


const useStyles = makeStyles()((theme) => ({
	header: {
		padding: theme.spacing(2, 3),
		backgroundColor: theme.palette.mode === 'dark' ? '#1c1c1c' : '#f5f5f5',
		borderBottom: `1px solid ${theme.palette.divider}`,
	},

	messagesWrapper: {
		flex: 1,
		overflow: 'hidden',
		display: 'flex',
		flexDirection: 'column',
	},

	inputContainer: {
		padding: theme.spacing(1.5, 3),
		backgroundColor: theme.palette.mode === 'dark' ? '#1c1c1c' : '#f5f5f5',
		borderTop: `1px solid ${theme.palette.divider}`,
		transition: 'all 0.3s ease',
		'&.expanded': {
			backgroundColor: theme.palette.mode === 'dark' ? '#3a3a2a' : '#fff9e6',
			padding: theme.spacing(2, 3),
		},
	},

	inputBox: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
		borderRadius: '24px',
		padding: theme.spacing(0.5, 1.5),
		transition: 'all 0.3s ease',
	},
}));

const MessagesReportModal = ({ open, onClose, ticket }) => {
	const { classes } = useStyles();
	const theme = useTheme();
	const history = useHistory();
	const { user } = React.useContext(AuthContext);

	const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
	const [loadingReschedule, setLoadingReschedule] = useState(false);
	const [inputMessage, setInputMessage] = useState("");
	const [sendingMessage, setSendingMessage] = useState(false);
	const [noteInputExpanded, setNoteInputExpanded] = useState(false);
	const [loadingUser, setLoadingUser] = useState(false);

	const handleOpenRescheduleModal = () => setRescheduleModalOpen(true);
	const handleCloseRescheduleModal = () => setRescheduleModalOpen(false);

	const handleRescheduleConfirm = async (formData) => {
		try {
			setLoadingReschedule(true);
			await api.post('/reschedule-tickets', {
				ticketId: ticket.id,
				...formData
			});
			ToastSuccess('Chamado reagendado com sucesso!');
			handleCloseRescheduleModal();
			history.push('/tickets');
		} catch (err) {
			ToastError(err);
		} finally {
			setLoadingReschedule(false);
		}
	};

	const handleSendNotepadMessage = async () => {
		if (inputMessage.trim() === "") return;

		const message = {
			read: 1,
			fromMe: true,
			mediaUrl: "",
			body: inputMessage.trim(),
			cleanMsg: inputMessage.trim(),
			quotedMsg: null,
			isText: true
		};

		try {
			setSendingMessage(true);
			await api.post(`/messagesNotepad/${ticket.id}`, message);
			ToastSuccess("Nota adicionada com sucesso!");
			setInputMessage("");
			setNoteInputExpanded(false);
		} catch (err) {
			ToastError(err);
		} finally {
			setSendingMessage(false);
		}
	};

	const setInputValue = (value) => {
		setInputMessage(inputMessage ? inputMessage + value : value);
	};

	const toggleNoteInput = () => {
		setNoteInputExpanded(!noteInputExpanded);
	};

	const getStatusColor = (status) => {
		const colors = {
			open: '#4caf50',
			pending: '#ff9800',
			closed: '#757575',
		};
		return colors[status] || colors.open;
	};

	return (
		<Dialog
			open={open}
			onClose={() => onClose(false)}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: {
					height: '90vh',
					maxHeight: '90vh',
					display: 'flex',
					flexDirection: 'column',
				}
			}}
		>
			{/* Header */}
			<Box className={classes.header}>
				<Stack direction="row" spacing={2} alignItems="center">
					{loadingUser ? (
						<Skeleton variant="circular" width={48} height={48} />
					) : (
						<Avatar
							src={ticket?.contact?.profilePicUrl}
							alt={ticket?.contact?.name}
							sx={{ width: 48, height: 48 }}
						/>
					)}

					<Box flex={1}>
						{loadingUser ? (
							<>
								<Skeleton width="60%" height={24} />
								<Skeleton width="40%" height={18} />
							</>
						) : (
							<>
								<Typography variant="h6" fontWeight={600}>
									{ticket?.contact?.name}
								</Typography>
								<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
									<Chip
										label={ticket?.status}
										size="small"
										sx={{
											bgcolor: getStatusColor(ticket?.status),
											color: '#fff',
											fontWeight: 600,
											textTransform: 'capitalize',
										}}
									/>
									{ticket?.user && (
										<Typography variant="caption" color="text.secondary">
											{ticket.user.name}
										</Typography>
									)}
									{ticket?.queue && (
										<Typography variant="caption" color="text.secondary">
											• {ticket.queue.name}
										</Typography>
									)}
									<Typography variant="caption" color="text.secondary">
										• #{ticket?.uuid || ticket?.id}
									</Typography>
								</Stack>
							</>
						)}
					</Box>

					<Stack direction="row" spacing={1}>
						{+user?.companyId === 2 && (
							<Tooltip title="Reagendar Chamado">
								<IconButton onClick={handleOpenRescheduleModal} color="primary" disabled={loadingReschedule}>
									<ScheduleSendIcon />
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title="Fechar">
							<IconButton onClick={() => onClose(false)}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</Box>

			{/* Messages Area - usando MessagesList component */}
			<Box className={classes.messagesWrapper}>
				{ticket && (
					<ReplyMessageContext.Provider value={{ setReplyingMessage: () => { } }}>
						<MessagesList
							ticket={ticket}
							ticketId={ticket.id}
							isGroup={ticket?.contact?.isGroup || false}
							isPreview={true}
						/>
					</ReplyMessageContext.Provider>
				)}
			</Box>

			{/* Input de Nota */}
			<Box className={`${classes.inputContainer} ${noteInputExpanded ? 'expanded' : ''}`}>
				{!noteInputExpanded ? (
					<Tooltip title="Adicionar Nota">
						<IconButton onClick={toggleNoteInput} color="warning">
							<NoteAltIcon />
						</IconButton>
					</Tooltip>
				) : (
					<Box className={classes.inputBox}>
						<Tooltip title="Fechar">
							<IconButton size="small" onClick={toggleNoteInput} color="warning">
								<NoteAltIcon fontSize="small" />
							</IconButton>
						</Tooltip>

						<EmojiPicker
							id="emojiPickerNotepad"
							disabled={sendingMessage}
							value={''}
							setValue={setInputValue}
						/>

						<InputBase
							fullWidth
							multiline
							maxRows={3}
							placeholder="Adicionar nota..."
							value={inputMessage}
							onChange={(e) => setInputMessage(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									handleSendNotepadMessage();
								}
							}}
							disabled={sendingMessage}
							autoFocus
						/>

						<IconButton
							color="primary"
							onClick={handleSendNotepadMessage}
							disabled={sendingMessage || inputMessage.trim() === ""}
							size="small"
						>
							{sendingMessage ? <CircularProgress size={20} /> : <SendIcon />}
						</IconButton>
					</Box>
				)}
			</Box>

			<RescheduleTicket
				open={rescheduleModalOpen}
				onClose={handleCloseRescheduleModal}
				onConfirm={handleRescheduleConfirm}
				ticket={ticket}
			/>
		</Dialog>
	);
};

export default MessagesReportModal;
